import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
import type { GenericEntity, GenericEntityItem } from './GenericEntities.types';

export interface GenericEntityModalProps {
    isOpen: boolean;
    mode: 'create' | 'edit';
    entity?: GenericEntity | null;
    initialItems?: GenericEntityItem[];
    onClose: () => void;
    onSave: (entity: Partial<GenericEntity>, items: GenericEntityItem[]) => void;
    isLoading?: boolean;
}

export const GenericEntityModal: React.FC<GenericEntityModalProps> = ({
    isOpen,
    mode,
    entity,
    initialItems = [],
    onClose,
    onSave,
    isLoading = false,
}) => {
    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [items, setItems] = useState<GenericEntityItem[]>([]);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && entity) {
                setCode(entity.code);
                setDescription(entity.description);
                setItems(initialItems);
            } else {
                setCode('');
                setDescription('');
                setItems([]);
            }
        }
    }, [isOpen, mode, entity, initialItems]);

    if (!isOpen) return null;

    const isEdit = mode === 'edit';
    const title = isEdit ? `Editar : ${entity?.description || ''}` : 'Nueva Entidad Genérica';

    const handleAddItem = () => {
        setItems([
            ...items,
            {
                id: Math.random().toString(), // ID temporal
                genericEntityId: entity?.id || '',
                code: '',
                value: '',
                sortOrder: items.length,
                isActive: true,
            },
        ]);
    };

    const handleUpdateItem = (index: number, field: keyof GenericEntityItem, val: any) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: val };
        setItems(newItems);
    };

    const handleRemoveItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const isValid = code.trim() !== '' && description.trim() !== '';

    const handleSave = () => {
        if (!isValid) return;
        onSave(
            {
                ...(entity || {}),
                code,
                description,
                isActive: entity ? entity.isActive : true,
            },
            items
        );
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-dark-bg-primary rounded-2xl shadow-xl border border-border-primary dark:border-dark-border-primary relative w-full max-w-4xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-gray-100 dark:bg-dark-bg-secondary flex justify-between items-center px-5 py-4 border-b border-border-primary dark:border-dark-border-primary rounded-t-2xl">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-dark-content-primary">
                        {title}
                    </h2>
                    <Button
                        type="plain"
                        size="xs"
                        iconOnly
                        leftIcon={<XMarkIcon className="w-4 h-4 text-content-primary dark:text-dark-content-primary" />}
                        ariaLabel="Cerrar"
                        onClick={onClose}
                        disabled={isLoading}
                        className="border border-border-custom-primary !p-2"
                    />
                </div>

                {/* Body */}
                <div className="p-5 flex flex-col gap-5 overflow-y-auto">
                    {/* Main Fields */}
                    <div className="flex gap-5">
                        <div className="flex w-[265px] flex-col gap-2">
                            <label className="text-sm font-medium text-gray-900 dark:text-dark-content-primary flex gap-1">
                                Código <span className="text-red-300">*</span>
                            </label>
                            <Input
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                disabled={isEdit} 
                            />
                        </div>
                        <div className="flex w-[265px] flex-col gap-2">
                            <label className="text-sm font-medium text-gray-900 dark:text-dark-content-primary flex gap-1">
                                Nombre <span className="text-red-300">*</span>
                            </label>
                            <Input
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Options (Variables) */}
                    <div className="flex flex-col gap-5 items-end">
                        <div className="flex justify-between items-center w-full">
                            <div className="bg-cyan-100 dark:bg-cyan-900/30 border border-cyan-400 dark:border-cyan-700 flex gap-2 items-center px-4 py-2 rounded-lg">
                                <InformationCircleIcon className="w-6 h-6 text-cyan-700 dark:text-cyan-400" />
                                <span className="text-sm text-cyan-700 dark:text-cyan-400">
                                    Es el listado de opciones que veras
                                </span>
                            </div>
                            <Button onClick={handleAddItem} type="outline" size="sm" className="font-medium">
                                Nuevo
                            </Button>
                        </div>

                        <div className="flex flex-col w-full">
                            <div className="flex h-9 border-b border-border-primary dark:border-dark-border-primary">
                                <div className="flex-1 flex items-center px-4 py-2 border-b border-border-primary dark:border-dark-border-primary">
                                    <span className="text-sm font-medium text-content-secondary dark:text-dark-content-secondary">
                                        Valor ID
                                    </span>
                                </div>
                                <div className="flex-1 flex items-center px-4 py-2 border-b border-border-primary dark:border-dark-border-primary">
                                    <span className="text-sm font-medium text-content-secondary dark:text-dark-content-secondary">
                                        Etiqueta
                                    </span>
                                </div>
                                <div className="w-[100px] flex items-center px-4 py-2 border-b border-border-primary dark:border-dark-border-primary">
                                    <span className="text-sm font-medium text-content-secondary dark:text-dark-content-secondary">
                                        Acciones
                                    </span>
                                </div>
                            </div>

                            {items.length === 0 ? (
                                <div className="p-8 text-center text-sm text-content-secondary dark:text-dark-content-secondary">
                                    No se han agregado opciones. Haz clic en "Nuevo" para comenzar.
                                </div>
                            ) : (
                                <div className="max-h-[300px] overflow-y-auto">
                                    {items.map((item, index) => (
                                        <div key={item.id} className="flex items-start gap-5 py-2 border-b border-border-secondary dark:border-dark-border-secondary w-full">
                                            <div className="flex-1 flex flex-col gap-2">
                                                <Input value={item.code} onChange={e => handleUpdateItem(index, 'code', e.target.value)} />
                                            </div>
                                            <div className="flex-1 flex flex-col gap-2">
                                                <Input value={item.value} onChange={e => handleUpdateItem(index, 'value', e.target.value)} />
                                            </div>
                                            <div className="w-[100px] h-8 flex justify-center items-center px-4">
                                                <Button type="plain" iconOnly leftIcon={<TrashIcon className="w-4 h-4 text-red-500" />} onClick={() => handleRemoveItem(index)} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-100 dark:bg-dark-bg-secondary flex justify-end px-5 py-4 border-t border-gray-100 dark:border-dark-border-primary rounded-b-2xl">
                     <Button
                        type="default"
                        disabled={!isValid || isLoading}
                        onClick={handleSave}
                        className="bg-[#0e79fd] hover:bg-blue-600 text-white font-medium px-4 py-2 rounded-md shadow-[inset_0px_2px_0px_0px_rgba(255,255,255,0.15)] border border-[#3c9bf6]"
                    >
                        {isLoading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
